import { useEffect, useState } from 'react';
import type { SpaceListItem } from '../api/auth';
import type { MenuItem, SubItem } from './platform-menu';
import { Button } from '../components/arc/button/button';
import { useFeather } from './useFeather';
import './mobile-menu.css';

interface Props {
  open: boolean;
  onClose: () => void;
  spaces: SpaceListItem[];
  activeSpace: SpaceListItem | null;
  onNavigateSpace: (subdomain: string | null) => void;
  menu: MenuItem[];
  activeModuleId: string;
  activeChildId?: string;
  onSelectModule: (item: MenuItem) => void;
  onSelectChild: (item: MenuItem, child: SubItem) => void;
  onOpenTasks: () => void;
  onOpenHelpdesk?: () => void;
  onOpenSettings: () => void;
  onLogout: () => void;
  displayName: string;
  email: string;
  showTasks?: boolean;
  showHelpdesk?: boolean;
}

function SpaceIcon({ space }: { space: SpaceListItem }) {
  const [failed, setFailed] = useState(false);
  if (space.slug === 'dmwk') {
    return <span className="p1-space-mark p1-space-mark-dmwk">D</span>;
  }
  if (space.slug && !failed) {
    return <img src={`/assets/spaces/${space.slug}.png`} alt="" onError={() => setFailed(true)} />;
  }
  return (
    <span className="avatar avatar-xs avatar-rounded avatar-primary">
      <span className="initial-wrap">{(space.name ?? 'S').charAt(0).toUpperCase()}</span>
    </span>
  );
}

export function MobileMenu({
  open,
  onClose,
  spaces,
  activeSpace,
  onNavigateSpace,
  menu,
  activeModuleId,
  activeChildId,
  onSelectModule,
  onSelectChild,
  onOpenTasks,
  onOpenHelpdesk,
  onOpenSettings,
  onLogout,
  displayName,
  email,
  showTasks = true,
  showHelpdesk = true,
}: Props) {
  const [expandedModuleId, setExpandedModuleId] = useState<string | null>(activeModuleId);
  useFeather([open, activeModuleId, activeChildId, expandedModuleId]);

  useEffect(() => {
    if (open) setExpandedModuleId(activeModuleId);
  }, [open, activeModuleId]);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add('p1-mm-open');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('p1-mm-open');
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <div id="p1-mm" aria-hidden={open ? 'false' : 'true'} role="dialog" aria-label="Mobilní menu">
      <div className="p1-mm-overlay" onClick={onClose} />
      <aside className="p1-mm-panel p1-arc-scope">
        <div className="p1-mm-head">
          <strong>Menu</strong>
          <Button type="button" variant="ghost" size="sm" className="p1-mm-close" onClick={onClose} aria-label="Zavřít menu"><i data-feather="x" /></Button>
        </div>

        <div className="p1-mm-scroll">
        <h6 className="p1-mm-spaces-heading">Spaces <small>{activeSpace?.name}</small></h6>
        <ul className="p1-mm-spaces">
          {spaces.length === 0 ? (
            <li className="p1-mm-no-spaces">Žádné Spaces</li>
          ) : (
            spaces.map((s) => (
              <li key={s.id}>
                <a
                  href="#"
                  className={s.id === activeSpace?.id ? 'active' : undefined}
                  aria-label={s.name}
                  aria-current={s.id === activeSpace?.id ? 'page' : undefined}
                  title={s.name}
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                    onNavigateSpace(s.subdomain);
                  }}
                >
                  <SpaceIcon space={s} />
                </a>
              </li>
            ))
          )}
        </ul>

        <h6>Stránky</h6>
        <ul>
          {menu.map((m) => {
            const hasChildren = Boolean(m.children?.length);
            const isExpanded = expandedModuleId === m.id;
            const moduleContent = (
              <>
                <span className="feather-icon"><i data-feather={m.icon} /></span>
                <span>{m.label}</span>
              </>
            );
            return (
              <li key={m.id}>
                {hasChildren ? (
                  <button
                    type="button"
                    className={`p1-mm-module-toggle${m.id === activeModuleId ? ' active' : ''}${isExpanded ? ' expanded' : ''}`}
                    aria-expanded={isExpanded}
                    onClick={() => setExpandedModuleId(isExpanded ? null : m.id)}
                  >
                    {moduleContent}
                    <span className="p1-mm-module-chevron"><i data-feather="chevron-down" /></span>
                  </button>
                ) : (
                  <a
                    href="#"
                    className={m.id === activeModuleId ? 'active' : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                      onSelectModule(m);
                    }}
                  >
                    {moduleContent}
                  </a>
                )}
                {hasChildren && isExpanded && (
                  <ul className="p1-mm-sub">
                    {m.children!.map((c) => (
                      <li key={c.id}>
                        <a
                          href="#"
                          className={c.id === activeChildId ? 'active' : undefined}
                          onClick={(e) => {
                            e.preventDefault();
                            onClose();
                            onSelectChild(m, c);
                          }}
                        >
                          <span>{c.label}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        {(showTasks || showHelpdesk) && (
          <>
            <h6>Nástroje</h6>
            <ul className="p1-mm-tools">
              {showTasks && (
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                      onOpenTasks();
                    }}
                  >
                    <span className="feather-icon">
                      <i data-feather="check-square"></i>
                    </span>
                    <span>Úkoly</span>
                  </a>
                </li>
              )}
              {showHelpdesk && (
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                      onOpenHelpdesk?.();
                    }}
                  >
                    <span className="feather-icon">
                      <i data-feather="life-buoy"></i>
                    </span>
                    <span>Helpdesk</span>
                  </a>
                </li>
              )}
            </ul>
          </>
        )}
        </div>

        <div className="p1-mm-account-bar" aria-label="Účet a nastavení">
          <span className="p1-mm-account-identity" title={`${displayName}${email ? ` · ${email}` : ''}`} aria-label={`${displayName}${email ? `, ${email}` : ''}`}><i data-feather="user" /></span>
          <Button type="button" variant="ghost" size="sm" className="p1-mm-account-action" onClick={() => { onClose(); onOpenSettings(); }} title="Nastavení" aria-label="Nastavení"><i data-feather="settings" /></Button>
          <Button type="button" variant="ghost" size="sm" className="p1-mm-account-action" onClick={() => { onClose(); onLogout(); }} title="Odhlásit se" aria-label="Odhlásit se"><i data-feather="log-out" /></Button>
        </div>
      </aside>
    </div>
  );
}
